import { lazy, type ComponentType } from 'react';

// Eagerly imported widgets (lightweight)
export { default as KPIWidget } from './KPIWidget';
export { default as ChartWidget } from './ChartWidget';
export { default as AlertListWidget } from './AlertListWidget';
export { default as MiniGraphWidget } from './MiniGraphWidget';
export { default as ActivityFeedWidget } from './ActivityFeedWidget';
export { default as RiskOverviewWidget } from './RiskOverviewWidget';
export { default as MapWidget } from './MapWidget';

// ── Widget size types ──

export type WidgetSize = '1x1' | '2x1' | '1x2' | '2x2';

export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  component: ComponentType<Record<string, unknown>>;
  defaultSize: WidgetSize;
  category: 'kpi' | 'chart' | 'list' | 'visualization' | 'feed';
}

// ── Widget Registry ──

export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  'kpi-entities': {
    id: 'kpi-entities',
    name: 'KPI Entites',
    description: 'Nombre total d\'entites dans le systeme',
    component: lazy(() => import('./KPIWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'kpi',
  },
  'kpi-alerts': {
    id: 'kpi-alerts',
    name: 'KPI Alertes',
    description: 'Nombre d\'alertes actives',
    component: lazy(() => import('./KPIWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'kpi',
  },
  'kpi-relationships': {
    id: 'kpi-relationships',
    name: 'KPI Relations',
    description: 'Nombre total de relations',
    component: lazy(() => import('./KPIWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'kpi',
  },
  'kpi-communities': {
    id: 'kpi-communities',
    name: 'KPI Communautes',
    description: 'Nombre de communautes detectees',
    component: lazy(() => import('./KPIWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'kpi',
  },
  'chart-activity': {
    id: 'chart-activity',
    name: 'Activite (24h)',
    description: 'Graphique d\'activite sur les dernieres 24 heures',
    component: lazy(() => import('./ChartWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '2x1',
    category: 'chart',
  },
  'chart-entity-distribution': {
    id: 'chart-entity-distribution',
    name: 'Distribution des entites',
    description: 'Repartition par type d\'entite',
    component: lazy(() => import('./ChartWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'chart',
  },
  'chart-severity': {
    id: 'chart-severity',
    name: 'Severite des alertes',
    description: 'Repartition des alertes par severite',
    component: lazy(() => import('./ChartWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'chart',
  },
  'alert-list': {
    id: 'alert-list',
    name: 'Alertes recentes',
    description: 'Liste des 10 dernieres alertes',
    component: lazy(() => import('./AlertListWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '2x1',
    category: 'list',
  },
  'mini-graph': {
    id: 'mini-graph',
    name: 'Apercu du graphe',
    description: 'Visualisation miniature du graphe d\'entites',
    component: lazy(() => import('./MiniGraphWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'visualization',
  },
  'activity-feed': {
    id: 'activity-feed',
    name: 'Flux d\'activite',
    description: 'Flux en temps reel des evenements',
    component: lazy(() => import('./ActivityFeedWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x2',
    category: 'feed',
  },
  'risk-overview': {
    id: 'risk-overview',
    name: 'Vue des risques',
    description: 'Top 10 des entites a haut risque',
    component: lazy(() => import('./RiskOverviewWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'list',
  },
  'map': {
    id: 'map',
    name: 'Carte geographique',
    description: 'Localisation des entites sur la carte',
    component: lazy(() => import('./MapWidget')) as unknown as ComponentType<Record<string, unknown>>,
    defaultSize: '1x1',
    category: 'visualization',
  },
};

export const getWidgetDefinition = (id: string): WidgetDefinition | undefined =>
  WIDGET_REGISTRY[id];

export const getWidgetsByCategory = (category: WidgetDefinition['category']): WidgetDefinition[] =>
  Object.values(WIDGET_REGISTRY).filter((w) => w.category === category);

export const getAllWidgets = (): WidgetDefinition[] => Object.values(WIDGET_REGISTRY);
